import { Question } from './sku.types';
import { Maybe } from 'types/util.types';
import { PartnerAPI } from '@integrations/partners/partners.types';
import { BREAKDOWN_PRICE_TYPES } from 'src/constants/cart';

export type Item = {
  questions: Question[];
  skuId: number;
  remote: boolean;
  remoteOnly: boolean;
  heroImageUrl: string;
  physicalProduct: boolean;
  hasUnansweredQuestions: boolean;
  autoAdded: boolean;
};

export type Address = {
  fullAddress: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  details: string;
};

export type CartPlan = {
  id: number;
  name: string;
  amount: number;
  truckRollFee: number;
  pricingType: string;
  planInterval: string;
};

export type Cart = {
  id: number;
  status: string;
  items: Item[];
  address: Address;
  lat: number;
  lng: number;
  phone: string;
  timezone: string;
  card: Card;
  partnerName: Maybe<string>;
  hasProduct: boolean;
  clientName: string;
  partner: Maybe<PartnerAPI>;
  plan?: CartPlan;
  remote: boolean;
  remotePossible: boolean;
  token: string;
  breakdown: BreakdownItem;
  affirmEligible: boolean;
  order: any; // for now
  fieldSaleAgent?: {
    id: number;
  };
  singleService: boolean;
  workflow?: string;
  // TODO: Type this properly when we have a use for avaialbility
  availability: any[];
  clientId: number | null;
  hasProducts: boolean;
  hasOnlyProducts: boolean;
  skipCart: boolean;
  statusId: number;
};

export enum Prepaid {
  None = 'none',
  Full = 'full',
  Base = 'base',
}

export type CartOrder = Maybe<{
  id: number;
  fromApi: boolean;
}> | null;

// Should match AdjustmentType in rails' cart.rb file
export enum AdjustmentType {
  REMOTE = 'remote',
  SUBSIDY = 'subsidy',
  ANSWER = 'answer',
  EXTRA_HOURS = 'EXTRA_HOURS',
}

export type ItemAdjustments = {
  amount: string | number;
  amountFormatted?: string;
  lineThroughText?: React.ReactNode;
  name: string;
  type: AdjustmentType;
};

export type BreakdownItem = {
  adjustments: ItemAdjustments[];
  amount: number;
  amountFormatted: string;
  amountWithoutSubsidy: number;
  amountWithoutSubsidyFormatted: string;
  category: string;
  image: string;
  isProduct: boolean;
  name: string;
  prepaid: Prepaid;
  priceType: keyof typeof BREAKDOWN_PRICE_TYPES;
  products: number[];
  remote: boolean;
  remoteOnly: boolean;
  requiresInstallation: boolean;
  savings: number;
  savingsFormatted: string;
  skuAmount: number;
  skuAmountFormatted: string;
  skuId: number;
  unlistedProducts?: null | number[];
  variantsIds: number[];
  [key: string]: any;
  // total: number;
  // totalSavings: number;
};

type Card = {
  id: number;
  lastDigits: number;
  brand: string;
  expMonth: string;
  expYear: string;
};

export type Plan = {
  id: number;
  name: string;
  amount: number;
  truckRollFee: number;
  pricingType: string;
  planInterval: string;
};

export type GlobalAdjustment = {
  name: string;
  amount: number;
  type: string;
  amountFormatted: string;
  description?: string;
  skuId?: number;
};

export type Coupon = {
  id: number;
  text: string;
  autoApply: boolean;
};

export type BreakDown = Partial<{
  adjustments: GlobalAdjustment[];
  subscriptionAdjustments: GlobalAdjustment[] | null | undefined;
  items: BreakdownItem[];
  itemsTotal: number; // total of only items array
  itemsTotalFormatted: string;
  serviceTax: number;
  serviceTaxFormatted: string;
  serviceTotal: number; // includes tax and global adjustments
  serviceTotalFormatted: string;
  subscriptionTax: number;
  subscriptionTaxFormatted: string;
  subscriptionTotal: number; // includes tax
  subscriptionTotalFormatted: string;
  total: number; // grand total
  totalFormatted: string;
  tax: number; // serviceTax and subscriptionTax
  taxFormatted: string;
  totalSavings: number; // savings with plan applied
  totalSavingsFormatted: string;
}>;

export type CartSkuToSelect = Pick<BreakdownItem, 'amountFormatted' | 'category' | 'isProduct' | 'name' | 'skuId' | 'variantsIds'> & {
  adjustments: {
    name: string;
    amount: string | undefined;
  }[];
  amount?: string | undefined;
  index: number;
  lineThroughText?: string | undefined;
};

export type SubscriptionPrices = {
  cheapestPrice: number;
  cheapestPriceFormatted: string;
};
